import React, { FC } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { IconMenu2, IconUserCircle } from "@tabler/icons-react";
import classNames from "classnames";
import { Box, Drawer } from "@mui/material";
import Button from "../ui/Button";
import Avatar from "./components/Avatar/Avatar";
import DropdownMenuUser from "./components/NavDropdownUsers/NavDropDownUsers";
import BurgerMenuDesktop from "./components/BurgerMenuDesktop/BurgerMenuDesktop";
import NavBarMobile from "./components/NavBarMobile/NavBarMobile";
import NavDropdown from "./components/NavDropdown/NavDropdown";
import MegaMenu from "./components/MegaMenu/MegaMenu";
import { NavSectionConfig, NavLink } from "./types/navbar";
import CountriesSelect, { CountriesType } from "./components/CountriesSelect/CountriesSelect";
import { NAVBAR_THEMES } from "./constants/themes";
import { useNavbarScroll } from "./hooks/useNavbarScroll";
import { useMegaMenu } from "./hooks/useMegaMenu";
import { useNavDrawer } from "./hooks/useNavDrawer";
import { useNavAuth } from "./hooks/useNavAuth";
import { NAVBAR_ASSETS } from "./constants/assets";

type Props = {
  navbarConfig: NavSectionConfig[]; // Datos de Contentful (navbarConfig)
  countries: CountriesType[];
  iconLinks: NavLink[];
  transparentRoutes?: string[];
};

// Rutas donde el navbar no debe mostrarse
const hideNavbarRoutes = [
  "/desarrolladora",
  "auth",
  "pago-rapido",
];

// Rutas donde el navbar usa el ancho completo
const fullWidthRoutes = [
  "/venta/casas-y-apartamentos",
  "/bienes-raices",
];

type AuthButtonProps = {
  isLogged: boolean;
  avatarSize: number;
  onAuthClick: () => void;
  loginIconBg: string;
};

const AuthButton = ({ isLogged, avatarSize, onAuthClick, loginIconBg }: AuthButtonProps) => {
  if (isLogged) {
    return (
      <button onClick={onAuthClick} type="button" className="flex focus:outline-none">
        <Avatar size={avatarSize} />
      </button>
    );
  }
  return (
    <button onClick={onAuthClick} type="button" className="focus:outline-none" aria-label="Iniciar sesión">
      <div className={classNames("flex items-center justify-center w-10 h-10 rounded-full", loginIconBg)}>
        <IconUserCircle width={22} height={22} className="text-white" />
      </div>
    </button>
  );
};

const NavBarMarket: FC<Props> = ({ navbarConfig, countries, iconLinks, transparentRoutes = [] }) => {
  const location = useLocation();
  const { country } = useParams<{ country: string }>();
  const searchParams = useParams();

  const shouldHideNavbar = hideNavbarRoutes.some((route) =>
    location.pathname.includes(route)
  );

  const fullWidth = fullWidthRoutes.some((route) => location.pathname.includes(route));

  const isTransparent = transparentRoutes.includes(location.pathname);

  const { navbarLogo: scrollLogo, isScrolled } = useNavbarScroll(isTransparent);
  const { logo } = NAVBAR_ASSETS;

  const sections = navbarConfig.filter((s) => s.desktopNav);
  const { openSection, slideDir, isSwitching, toggleSection, closeSection } = useMegaMenu(sections);
  const { isLogged, doShowNav } = useNavAuth(country);
  const { openDrawer, openBurger, toggleDrawer, toggleBurger, closeBurger } = useNavDrawer(isLogged);

  const navbarLogo = openSection ? logo : scrollLogo;
  const isDark = !isTransparent || isScrolled;
  const mode = (isDark || openSection) ? "light" : "transparent" as const;
  const theme = NAVBAR_THEMES[mode];

  if (shouldHideNavbar) {
    return null;
  }

  return (
    <>
      <header
        id="navbar"
        className={classNames(
          "z-50",
          !openSection && "duration-300 transition-all",
          isTransparent ? "fixed top-0 w-full" : "sticky top-0",
          openSection ? theme.bgOpen : theme.bg,
        )}
      >
        <div className={classNames(
          "py-3 lg:py-4 px-4 lg:px-0",
          fullWidth ? "w-full px-4 lg:px-8" : "mx-auto max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-7xl",
        )}>
          <div className="flex items-center justify-between">

            {/* Logo + nav links */}
            <div className="flex items-center gap-2">
              <Link to={`/${country}`} className="flex items-center" aria-label="Ir al inicio">
                <img src={navbarLogo} className="h-6 sm:h-9 w-auto mr-3" alt="Logo Propi" />
              </Link>
              <nav aria-label="Navegación principal" className="hidden lg:flex items-center gap-1">
                {sections.map((section) => (
                  <NavDropdown
                    key={section.title}
                    section={section}
                    mode={mode}
                    isOpen={openSection?.title === section.title}
                    onToggle={section.megaMenu ? () => toggleSection(section) : closeSection}
                  />
                ))}
              </nav>
            </div>

            {/* Desktop right side */}
            <div className="hidden lg:flex items-center gap-3">
              {!searchParams?.id && !searchParams?.code && (
                <CountriesSelect mode={mode} countries={countries} />
              )}
              <div className="relative flex items-center gap-3">
                <AuthButton isLogged={isLogged} avatarSize={39} onAuthClick={doShowNav} loginIconBg={theme.loginIconBg} />
                <div className={classNames("w-px h-8", theme.divider)} />
                <div className="relative">
                  <button
                    type="button"
                    aria-label="Abrir menú"
                    aria-expanded={openBurger}
                    onClick={toggleBurger}
                    className="flex focus:outline-none"
                  >
                    <IconMenu2 size={22} className={theme.text} />
                  </button>
                  {openBurger && (
                    <BurgerMenuDesktop mode={mode} onClose={closeBurger} navConfig={navbarConfig} />
                  )}
                </div>
                <DropdownMenuUser />
              </div>
            </div>

            {/* Mobile right side */}
            <div className="flex lg:hidden items-center gap-2">
              <div className="relative">
                <AuthButton isLogged={isLogged} avatarSize={32} onAuthClick={doShowNav} loginIconBg={theme.loginIconBg} />
                {isLogged && <DropdownMenuUser />}
              </div>
              <div className={theme.iconBorder}>
                <Button
                  onClick={toggleDrawer}
                  variant="transparent"
                  size="md"
                  as="icon"
                  aria-label="Abrir menú"
                  aria-expanded={openDrawer}
                  className="!text-inherit hover:!text-primary active:!text-primary focus:!text-primary"
                >
                  <IconMenu2 />
                </Button>
              </div>
            </div>

          </div>
        </div>
        <AnimatePresence>
          {openSection && (
            <motion.div
              key="megamenu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="absolute left-0 right-0 top-full z-50 overflow-hidden"
            >
              <MegaMenu
                section={openSection}
                content={openSection?.megaMenu ?? null}
                slideDir={slideDir}
                isSwitching={isSwitching}
                mode={mode}
                onClose={closeSection}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={toggleDrawer}
        className="w-full"
        sx={{
          zIndex: 10001,
          "& .MuiDrawer-paper": {
            backgroundColor: "transparent",
            boxShadow: "none",
            width: "85vw",
            maxWidth: 360,
            height: "100%",
          },
        }}
      >
        <Box sx={{ width: "100%", height: "100%", backgroundColor: "transparent" }} role="presentation">
          <NavBarMobile onAction={toggleDrawer} countries={countries} navConfig={navbarConfig} iconLinks={iconLinks} />
        </Box>
      </Drawer>
    </>
  );
};

export default NavBarMarket;
